import React, { useMemo } from "react";
import { Badge } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { EXPENSE_SCHEDULE_STATUS } from "@/utils/api/ExpenseScheduleService";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface ExpenseScheduleStatusBadgeProps {
  /** The status value from EXPENSE_SCHEDULE_STATUS */
  status: number | undefined;

  /** Badge size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether to show the icon */
  showIcon?: boolean;
}

// ============================================
// Component
// ============================================

const ExpenseScheduleStatusBadgeComponent: React.FC<ExpenseScheduleStatusBadgeProps> = ({
  status,
  size = "md",
  showIcon = true,
}) => {
  const { t } = useTranslation(["expenseSchedules"]);

  const config = useMemo(() => {
    switch (status) {
      case EXPENSE_SCHEDULE_STATUS.ACTIVE:
        return {
          color: "green",
          label: t("expenseSchedules:status.active"),
          icon: "PlayerPlay",
        };
      case EXPENSE_SCHEDULE_STATUS.PAUSED:
        return {
          color: "yellow",
          label: t("expenseSchedules:status.paused"),
          icon: "PlayerPause",
        };
      case EXPENSE_SCHEDULE_STATUS.COMPLETED:
        return {
          color: "gray",
          label: t("expenseSchedules:status.completed"),
          icon: "Check",
        };
      default:
        return {
          color: "gray",
          label: t("expenseSchedules:status.unknown"),
          icon: "HelpCircle",
        };
    }
  }, [status, t]);

  // Determine icon size based on badge size
  const iconSize = useMemo(() => {
    switch (size) {
      case "xs":
        return 10;
      case "sm":
        return 12;
      case "md":
        return 14;
      case "lg":
        return 16;
      case "xl":
        return 18;
      default:
        return 12;
    }
  }, [size]);

  return (
    <Badge
      color={config.color}
      variant="light"
      size={size}
      leftSection={
        showIcon ? <AppIcon iconName={config.icon} size={iconSize} /> : undefined
      }
    >
      {config.label}
    </Badge>
  );
};

export const ExpenseScheduleStatusBadge = React.memo(ExpenseScheduleStatusBadgeComponent);